import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChange,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { isMasked } from '@miq/utils';
import { environment } from '@env/environment';
import { Column } from '@miq/catalog';
import { XColumn } from '@records/catalog/extension.model';
import {
  MiqBehaviors,
  MiqRegisteredBehaviorType,
  MiqTouchable,
  MiqValidatable,
  validateElements,
} from '@shared/behaviors';
import { getLovValueKey } from '@shared/utils/helpers/lov-datakey-factory';
import { getEntityId, validateUomFields } from '@shared/utils/helpers/records.helpers';

@Component({
  selector: 'miq-uom-record-detail-inputs',
  templateUrl: './uom-record-detail-inputs.component.html',
  styleUrls: ['./uom-record-detail-inputs.component.scss'],
  providers: [{ provide: MiqBehaviors, useExisting: UomRecordDetailInputsComponent }],
  standalone: false,
})
export class UomRecordDetailInputsComponent implements OnInit, OnChanges, MiqValidatable, MiqTouchable, OnDestroy {
  @Input() recordDetail: XColumn;
  @Input() isNewRecord: boolean;
  @Input() isDisabled: boolean;
  @Input() record: any;
  @Input() isRecordDetail: boolean;
  @Input() module = 'records';

  @Output() rdChange = new EventEmitter<any>();

  @ViewChildren(MiqBehaviors) elementsToValidate: QueryList<MiqBehaviors>;

  private valid = true;

  ngOnChanges({ record }: SimpleChanges): void {
    const notFirstChange = (x: SimpleChange) => !!x && !x.firstChange;
    if (
      notFirstChange(record) &&
      // if record changes and id aren different between prev and current,
      // then validate masking value
      getEntityId(record.previousValue) !== getEntityId(record.currentValue)
    ) {
      this.ensureDataMasking();
    }
  }

  ngOnInit() {
    this.ensureDataMasking();
  }

  ngOnDestroy() {
    this.elementsToValidate = null;
  }

  /**
   * Makes column and unit masked if any
   */
  private ensureDataMasking() {
    // due some records don't fully have either value or uom masked
    const values = [this.recordDetail.value, this.recordDetail.unit.value];
    const anyMasked = values.some(isMasked);
    const anyUnmasked = !values.every(isMasked);
    // if at least one is masked but not all
    if (anyMasked && anyUnmasked) {
      this.recordDetail.value = environment.appConstants.MASKING_TOKEN;
      this.recordDetail.unit.value = environment.appConstants.MASKING_TOKEN;
    }
  }

  private get touchableElements() {
    return this.elementsToValidate.toArray().ofType(MiqRegisteredBehaviorType.Touchable);
  }

  private get validatablelements() {
    return this.elementsToValidate.toArray().ofType(MiqRegisteredBehaviorType.Validatable);
  }

  fillAssociatedUom(inputRd) {
    if (inputRd.value != null && !this.recordDetail.unit.value) {
      // we only patch & emit when value is present
      // and uom is empty
      const dataKey = getLovValueKey(this.recordDetail.unit);
      const [firstOption] = this.recordDetail.unit.values;
      this.recordDetail.unit.value = firstOption ? firstOption[dataKey] : null;
      this.rdChange.emit(this.recordDetail.unit);
    } else if (inputRd.value == null && !!this.recordDetail.unit.value) {
      this.recordDetail.unit.value = null;
      this.rdChange.emit(this.recordDetail.unit);
    }
  }

  validateAndEmitInput(recordDetail) {
    this.rdChange.emit(recordDetail);
    this.fillAssociatedUom(recordDetail);
    this.validate(recordDetail, recordDetail.unit);
  }

  validateAndEmitUom() {
    this.validate(this.recordDetail, this.recordDetail.unit);
    this.touchableElements.filter(element => !element.isTouched()).forEach(element => element.markAsTouched());
    this.rdChange.emit(this.recordDetail.unit);
  }

  validate(input: Column, uom: Column) {
    this.valid = validateUomFields(input, uom) && validateElements(this.validatablelements);
  }

  isValid(): boolean {
    if (this.isTouched()) {
      this.validate(this.recordDetail, this.recordDetail.unit);
      return this.valid && validateElements(this.validatablelements);
    }
    return validateElements(this.validatablelements);
  }

  isTouched() {
    return this.touchableElements.some(element => element.isTouched());
  }

  markAsTouched() {
    this.touchableElements.forEach(element => element.markAsTouched());
  }
}
